import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X, ChevronDown, ChevronUp, Copy, Check, Mail } from 'lucide-react';
import { t } from '../../i18n/t';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORT_EMAIL } from '../../config/support';
import {
  dismissToast,
  getToastsSnapshot,
  setToastUserContext,
  subscribeToasts,
  type ErrorToastItem,
} from './errorToastStore';
import './ErrorToast.css';

const MAIL_BODY_MAX_ENCODED = 1800;

export function buildMailto(item: ErrorToastItem): string {
  const screen = window.location.pathname;
  const subject = t('errorToast.mailSubject', { title: item.title, screen });
  const tail = `\n\n${t('errorToast.mailPrompt')}\n`;
  const fits = (s: string) => encodeURIComponent(s + tail).length <= MAIL_BODY_MAX_ENCODED;
  let tech = item.technical;
  if (!fits(tech)) {
    const note = `\n${t('errorToast.mailTruncated')}`;
    let lo = 0;
    let hi = tech.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (fits(tech.slice(0, mid) + note)) lo = mid;
      else hi = mid - 1;
    }
    tech = tech.slice(0, lo) + note;
  }
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(tech + tail)}`;
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // fall through to the legacy path (insecure origin / permission denied)
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function ToastCard({ item }: { item: ErrorToastItem }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const detailsId = `lop-toast-details-${item.id}`;

  useEffect(() => {
    if (!copied) return undefined;
    const h = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(h);
  }, [copied]);

  const onCopy = useCallback(async () => {
    await copyText(item.technical);
    setCopied(true);
  }, [item.technical]);

  return (
    <div
      className="lop-toast"
      role="alert"
      aria-live="assertive"
      data-testid="error-toast"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          dismissToast(item.id);
        }
      }}
    >
      <AlertCircle className="lop-toast__icon" size={20} aria-hidden="true" />
      <div className="lop-toast__main">
        <div className="lop-toast__head">
          <div className="lop-toast__title">
            {item.title}
            {item.count > 1 && (
              <span className="lop-toast__count" data-testid="error-toast-count">
                {'×'}{item.count}
              </span>
            )}
          </div>
          <button
            type="button"
            className="lop-toast__close"
            aria-label={t('errorToast.close')}
            onClick={() => dismissToast(item.id)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="lop-toast__body" data-testid="error-toast-body">{item.body}</div>
        <div className="lop-toast__actions">
          <button
            type="button"
            className="lop-toast__btn"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            {open ? t('errorToast.hideDetails') : t('errorToast.details')}
          </button>
          <button type="button" className="lop-toast__btn" onClick={() => void onCopy()}>
            {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
            {copied ? t('errorToast.copied') : t('errorToast.copy')}
          </button>
          <a className="lop-toast__btn" href={buildMailto(item)}>
            <Mail size={14} aria-hidden="true" />
            {t('errorToast.mail')}
          </a>
        </div>
        {open && (
          <pre
            id={detailsId}
            className="lop-toast__details"
            dir="ltr"
            tabIndex={0}
            data-testid="error-toast-details"
          >
            {item.technical}
          </pre>
        )}
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children?: React.ReactNode }) {
  const { user } = useAuth();
  const items = useSyncExternalStore(subscribeToasts, getToastsSnapshot, getToastsSnapshot);

  useEffect(() => {
    setToastUserContext(
      user?.email ?? null,
      user?.currentTenant ? `${user.currentTenant.tenantId} (${user.currentTenant.displayName})` : null,
    );
  }, [user]);

  return (
    <>
      {children}
      {createPortal(
        <div className="lop-toast-stack" role="region" aria-label={t('errorToast.regionLabel')}>
          {items.map((item) => (
            <ToastCard key={item.id} item={item} />
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
